import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building, Mail, Globe, Server, Shield, Scale, FileText, Gavel } from 'lucide-react';

export default function Impresszum() {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
        >
          <ArrowLeft size={20} />
          Vissza a főoldalra
        </button>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">IMPRESSZUM</h1>
            <p className="text-xl text-gray-600">ToldFel SaaS Szolgáltatás</p>
            <div className="mt-4 text-sm text-gray-500">
              <p>Hatályos: 2026.01.24.</p>
              <p>Verzió: 1.0</p>
            </div>
          </div>

          {/* Tartalomjegyzék */}
          <div className="bg-gray-50 rounded-lg p-6 mb-10">
            <h2 className="text-lg font-bold text-gray-900 mb-4">TARTALOMJEGYZÉK</h2>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                { id: 'section-1', title: '1. A Szolgáltató azonosító adatai' },
                { id: 'section-2', title: '2. Kapcsolattartási adatok' },
                { id: 'section-3', title: '3. Tárhelyszolgáltatók adatai' },
                { id: 'section-4', title: '4. Domain és hosting információk' },
                { id: 'section-5', title: '5. Szellemi tulajdon és szerzői jogok' },
                { id: 'section-6', title: '6. Felelősségkizárás' },
                { id: 'section-7', title: '7. Jogérvényesítési lehetőségek' },
                { id: 'section-8', title: '8. Kapcsolódó dokumentumok' },
                { id: 'section-9', title: '9. Vonatkozó jogszabályok' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-blue-600 hover:text-blue-800 hover:underline py-1"
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            {/* 1. A SZOLGÁLTATÓ AZONOSÍTÓ ADATAI */}
            <section id="section-1" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Building className="text-blue-600" size={28} />
                1. A SZOLGÁLTATÓ AZONOSÍTÓ ADATAI
              </h2>

              <p className="text-gray-700 mb-6">
                A <a href="https://toldfel.hu" className="text-blue-600 hover:underline">https://toldfel.hu</a> weboldalon 
                (a továbbiakban: <strong>Weboldal</strong>) elérhető ToldFel szoftver mint szolgáltatás (SaaS) üzemeltetője 
                (a továbbiakban: <strong>Szolgáltató</strong>):
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">Vállalkozó neve</td>
                      <td className="border border-gray-300 px-4 py-3">Tóth Máté egyéni vállalkozó</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Székhely</td>
                      <td className="border border-gray-300 px-4 py-3">2040 Budaörs, Mező utca 8/3.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Nyilvántartási szám</td>
                      <td className="border border-gray-300 px-4 py-3">7703390</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Adószám</td>
                      <td className="border border-gray-300 px-4 py-3">59654931-1-33</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Nyilvántartásba vétel napja</td>
                      <td className="border border-gray-300 px-4 py-3">2022.11.04.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Nyilvántartó hatóság</td>
                      <td className="border border-gray-300 px-4 py-3">Pest Vármegyei Kormányhivatal</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* 2. KAPCSOLATTARTÁSI ADATOK */}
            <section id="section-2" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Mail className="text-blue-600" size={28} />
                2. KAPCSOLATTARTÁSI ADATOK
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">2.1. Általános elérhetőségek</h3>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">E-mail cím</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="mailto:toldf3l@gmail.com" className="text-blue-600 hover:underline">toldf3l@gmail.com</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Telefonszám</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="tel:+36703927719" className="text-blue-600 hover:underline">+36 70 392 7719</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Weboldal</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://toldfel.hu" className="text-blue-600 hover:underline">https://toldfel.hu</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Ügyfélszolgálat</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="mailto:toldf3l@gmail.com" className="text-blue-600 hover:underline">toldf3l@gmail.com</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">2.2. Ügyfélszolgálati információk</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Kapcsolatfelvétel módja:</strong> Elektronikus úton, e-mailben</li>
                <li><strong>Ügyfélszolgálati e-mail:</strong> <a href="mailto:toldf3l@gmail.com" className="text-blue-600 hover:underline">toldf3l@gmail.com</a></li>
                <li><strong>Válaszadási idő:</strong> Munkanapokon 48 órán belül</li>
                <li><strong>Ügyfélszolgálat nyelve:</strong> Magyar</li>
              </ul>
            </section>

            {/* 3. TÁRHELYSZOLGÁLTATÓK ADATAI */}
            <section id="section-3" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Server className="text-blue-600" size={28} />
                3. TÁRHELYSZOLGÁLTATÓK ADATAI
              </h2>

              <p className="text-gray-700 mb-6">
                A Weboldal és a Szolgáltatás az alábbi tárhelyszolgáltatók infrastruktúráján működik:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">3.1. Frontend hosting</h3>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">Szolgáltató neve</td>
                      <td className="border border-gray-300 px-4 py-3">Oracle Corporation</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Szolgáltató székhelye</td>
                      <td className="border border-gray-300 px-4 py-3">2300 Oracle Way, Austin, TX 78741, USA</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Európai adatközpont</td>
                      <td className="border border-gray-300 px-4 py-3"><strong>Stockholm, Svédország (EU)</strong></td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Weboldal</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://www.oracle.com/cloud/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">oracle.com/cloud</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Adatvédelmi információk</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://www.oracle.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">oracle.com/legal/privacy</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">3.2. Backend és adatbázis</h3>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">Szolgáltató neve</td>
                      <td className="border border-gray-300 px-4 py-3">Supabase Inc.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Szolgáltató székhelye</td>
                      <td className="border border-gray-300 px-4 py-3">970 Toa Payoh North #07-04, Singapore 318992</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Európai adatközpont</td>
                      <td className="border border-gray-300 px-4 py-3"><strong>Stockholm, Svédország (EU)</strong></td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Weboldal</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">supabase.com</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Adatvédelmi információk</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">supabase.com/privacy</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">3.3. Fizetési szolgáltató</h3>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">Szolgáltató neve</td>
                      <td className="border border-gray-300 px-4 py-3">Stripe, Inc.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">EU képviselet</td>
                      <td className="border border-gray-300 px-4 py-3">Stripe Payments Europe, Limited (Írország)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Adatkezelés helye</td>
                      <td className="border border-gray-300 px-4 py-3"><strong>Dublin, Írország (EU)</strong></td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Megfelelőség</td>
                      <td className="border border-gray-300 px-4 py-3">PCI DSS Level 1, EU-US Data Privacy Framework</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Adatvédelmi információk</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">stripe.com/privacy</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  <strong>GDPR megfelelőség:</strong> Valamennyi fent említett szolgáltató megfelel a GDPR követelményeinek, 
                  és alkalmazza az Európai Bizottság által jóváhagyott általános szerződési feltételeket (Standard Contractual Clauses, SCC) 
                  vagy az EU-US Data Privacy Framework keretrendszert.
                </p>
              </div>
            </section>

            {/* 4. DOMAIN ÉS HOSTING INFORMÁCIÓK */}
            <section id="section-4" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Globe className="text-blue-600" size={28} />
                4. DOMAIN ÉS HOSTING INFORMÁCIÓK
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">4.1. Domain adatok</h3>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50 w-1/3">Domain név</td>
                      <td className="border border-gray-300 px-4 py-3">toldfel.hu</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Domain tulajdonos</td>
                      <td className="border border-gray-300 px-4 py-3">Tóth Máté egyéni vállalkozó</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Domain regisztrátor</td>
                      <td className="border border-gray-300 px-4 py-3">[Rackhost Zrt.]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Regisztrátor székhelye</td>
                      <td className="border border-gray-300 px-4 py-3">[6722 Szeged, Tisza Lajos körút 41.]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-semibold bg-gray-50">Regisztrátor weboldala</td>
                      <td className="border border-gray-300 px-4 py-3">[www.rackhost.hu]</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">4.2. Technikai infrastruktúra</h3>
              <p className="text-gray-700 mb-4">A Szolgáltatás az alábbi technikai infrastruktúrán működik:</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Frontend</h4>
                  <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                    <li>React.js alkalmazás</li>
                    <li>Tailwind CSS</li>
                    <li>Oracle Cloud Infrastructure</li>
                    <li>Régió: Stockholm, EU</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Backend</h4>
                  <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                    <li>Node.js szerver</li>
                    <li>Supabase (PostgreSQL)</li>
                    <li>Supabase Auth</li>
                    <li>Régió: Stockholm, EU</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Integrációk</h4>
                  <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                    <li>Facebook Graph API</li>
                    <li>YouTube Data API v3</li>
                    <li>TikTok Content Posting API</li>
                    <li>Stripe Payments API</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Biztonság</h4>
                  <ul className="list-disc pl-4 text-gray-700 text-sm space-y-1">
                    <li>HTTPS/TLS 1.3 titkosítás</li>
                    <li>OAuth 2.0 autentikáció</li>
                    <li>Row Level Security (RLS)</li>
                    <li>Titkosított adattárolás</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 5. SZELLEMI TULAJDON ÉS SZERZŐI JOGOK */}
            <section id="section-5" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Shield className="text-blue-600" size={28} />
                5. SZELLEMI TULAJDON ÉS SZERZŐI JOGOK
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">5.1. Szerzői jogi nyilatkozat</h3>
              <p className="text-gray-700 mb-4">
                A Weboldal és a Szolgáltatás, beleértve annak teljes tartalmát, megjelenését, grafikai elemeit, szövegeit, 
                logóját, védjegyeit, szoftverét, forráskódját és minden egyéb alkotórészét (a továbbiakban együttesen: <strong>Tartalom</strong>) 
                a Szolgáltató kizárólagos szellemi tulajdonát képezi, vagy a Szolgáltató jogszerűen felhasználja azokat.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">5.2. Védjegyek és logók</h3>
              <p className="text-gray-700 mb-4">
                A „<strong>ToldFel</strong>" elnevezés, logó és minden kapcsolódó grafikai elem a Szolgáltató védjegye vagy 
                szerzői jogi oltalom alatt álló alkotása. Ezek felhasználása a Szolgáltató előzetes írásbeli engedélye nélkül tilos.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">5.3. Felhasználási korlátozások</h3>
              <p className="text-gray-700 mb-4">A Tartalom bármely formában történő:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>másolása,</li>
                <li>sokszorosítása,</li>
                <li>terjesztése,</li>
                <li>nyilvános közlése,</li>
                <li>módosítása,</li>
                <li>átdolgozása,</li>
                <li>kereskedelmi célú felhasználása</li>
              </ul>
              <p className="text-gray-700 mb-4">
                kizárólag a Szolgáltató előzetes írásbeli hozzájárulásával megengedett.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">5.4. Felhasználói jogok</h3>
              <p className="text-gray-700 mb-4">
                A Szolgáltatás regisztrált felhasználói jogosultak a Szolgáltatás rendeltetésszerű használatára az 
                Általános Szerződési Feltételekben (ÁSZF) meghatározott keretek között. Ez a használati jog nem ruházható át, 
                nem kizárólagos, és a szerződés megszűnésével automatikusan megszűnik.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">5.5. Jogsértés jogkövetkezményei</h3>
              <p className="text-gray-700 mb-4">A szellemi tulajdonjogok megsértése esetén a Szolgáltató jogosult:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>a jogsértés azonnali abbahagyását követelni,</li>
                <li>kártérítési igényt érvényesíteni,</li>
                <li>büntető- és polgári jogi eljárást kezdeményezni,</li>
                <li>a jogsértő felhasználói fiókjának azonnali felfüggesztésére vagy törlésére.</li>
              </ul>
            </section>

            {/* 6. FELELŐSSÉGKIZÁRÁS */}
            <section id="section-6" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Scale className="text-blue-600" size={28} />
                6. FELELŐSSÉGKIZÁRÁS
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.1. Szolgáltatás elérhetősége</h3>
              <p className="text-gray-700 mb-4">
                A Szolgáltató törekszik a Weboldal és a Szolgáltatás folyamatos, zavartalan elérhetőségének biztosítására, 
                azonban nem vállal felelősséget az alábbi esetekben bekövetkező szolgáltatáskiesésért vagy korlátozásért:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Tervezett karbantartási munkálatok (amelyekről a Szolgáltató előzetesen tájékoztatást nyújt)</li>
                <li>Vis maior események (természeti katasztrófák, háború, sztrájk, járvány stb.)</li>
                <li>Harmadik fél által okozott technikai problémák (internetszolgáltató, tárhelyszolgáltató hibája)</li>
                <li>A felhasználó oldalán felmerülő technikai problémák</li>
                <li>Kibertámadások, DDoS támadások</li>
                <li>A közösségi platformok (Facebook, YouTube, TikTok) API-jainak elérhetetlensége vagy megváltozása</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.2. Tartalom pontossága</h3>
              <p className="text-gray-700 mb-4">
                A Weboldalon közzétett információk kizárólag tájékoztató jellegűek. A Szolgáltató törekszik azok pontosságára 
                és naprakészségére, azonban nem vállal felelősséget az esetleges pontatlanságokból, hiányosságokból eredő károkért.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.3. Külső hivatkozások</h3>
              <p className="text-gray-700 mb-4">
                A Weboldal tartalmazhat harmadik felek weboldalára mutató hivatkozásokat. A Szolgáltató nem vállal felelősséget:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>ezen weboldalak tartalmáért,</li>
                <li>az ott elérhető szolgáltatásokért,</li>
                <li>az ott kezelt adatok biztonságáért,</li>
                <li>az ezen oldalakon történő tranzakciókért.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.4. Felhasználói tartalom</h3>
              <p className="text-gray-700 mb-4">
                A Szolgáltató nem vállal felelősséget a Felhasználók által feltöltött videók és egyéb tartalmak:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>jogszerűségéért,</li>
                <li>harmadik felek jogait sértő jellegéért,</li>
                <li>a közösségi platformok irányelveit sértő tartalmáért,</li>
                <li>a platformok általi elutasításáért vagy törléséért.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.5. Közvetett károk</h3>
              <p className="text-gray-700 mb-4">
                A Szolgáltató a jogszabályok által megengedett legteljesebb mértékben kizárja felelősségét minden közvetett, 
                következményes, eseti kárért, elmaradt haszonért, üzleti lehetőség elvesztéséért.
              </p>
            </section>

            {/* 7. JOGÉRVÉNYESÍTÉSI LEHETŐSÉGEK */}
            <section id="section-7" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <Gavel className="text-blue-600" size={28} />
                7. JOGÉRVÉNYESÍTÉSI LEHETŐSÉGEK
              </h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">7.1. Panaszkezelés</h3>
              <p className="text-gray-700 mb-4">
                Amennyiben a Szolgáltatással vagy a Weboldallal kapcsolatban panasza merül fel, kérjük, forduljon a Szolgáltatóhoz:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-700"><strong>E-mail:</strong> <a href="mailto:toldf3l@gmail.com" className="text-blue-600 hover:underline">toldf3l@gmail.com</a></p>
                <p className="text-gray-700"><strong>Válaszadási határidő:</strong> 30 nap</p>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">7.2. Fogyasztóvédelmi hatóság</h3>
              <p className="text-gray-700 mb-4">
                Fogyasztóvédelmi panaszával a területileg illetékes járási hivatalhoz fordulhat:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Pest Vármegyei Kormányhivatal</h4>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li><strong>Cím:</strong> 1052 Budapest, Városház utca 7.</li>
                  <li><strong>Weboldal:</strong> <a href="https://www.kormanyhivatal.hu/hu/pest" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">kormanyhivatal.hu/hu/pest</a></li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">7.3. Békéltető testület</h3>
              <p className="text-gray-700 mb-4">Fogyasztói jogvita esetén békéltető testülethez fordulhat:</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Pest Vármegyei Kereskedelmi és Iparkamara mellett működő Békéltető Testület</h4>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li><strong>Cím:</strong> 1055 Budapest, Balassi Bálint utca 25. IV. em. 2.</li>
                  <li><strong>Telefon:</strong> +36 1 792 7881</li>
                  <li><strong>E-mail:</strong> <a href="mailto:pmbekelteto@pmkik.hu" className="text-blue-600 hover:underline">pmbekelteto@pmkik.hu</a></li>
                  <li><strong>Weboldal:</strong> <a href="https://www.panaszrendezes.hu" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">panaszrendezes.hu</a></li>
                </ul>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Online vitarendezési platform (ODR):</strong> Az Európai Bizottság online vitarendezési platformja az alábbi címen érhető el: 
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">ec.europa.eu/consumers/odr</a>
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">7.4. Adatvédelmi hatóság</h3>
              <p className="text-gray-700 mb-4">
                Adatvédelmi panaszával a Nemzeti Adatvédelmi és Információszabadság Hatósághoz (NAIH) fordulhat:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Nemzeti Adatvédelmi és Információszabadság Hatóság</h4>
                <ul className="text-gray-700 space-y-1 text-sm">
                  <li><strong>Cím:</strong> 1055 Budapest, Falk Miksa utca 9–11.</li>
                  <li><strong>Postacím:</strong> 1363 Budapest, Pf. 9.</li>
                  <li><strong>Telefon:</strong> +36 1 391 1400</li>
                  <li><strong>E-mail:</strong> <a href="mailto:ugyfelszolgalat@naih.hu" className="text-blue-600 hover:underline">ugyfelszolgalat@naih.hu</a></li>
                  <li><strong>Weboldal:</strong> <a href="https://www.naih.hu" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">naih.hu</a></li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">7.5. Bírósági jogérvényesítés</h3>
              <p className="text-gray-700 mb-4">
                Amennyiben a fenti lehetőségek nem vezetnek eredményre, jogait bíróság előtt is érvényesítheti. 
                A jogviták elbírálására a magyar bíróságok rendelkeznek joghatósággal.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">A Szolgáltató székhelye szerint illetékes bíróság:</h4>
                <p className="text-gray-700"><strong>Budaörsi Járásbíróság</strong></p>
                <p className="text-gray-700 text-sm">Cím: 2040 Budaörs, Szabadság út 134.</p>
              </div>
            </section>

            {/* 8. KAPCSOLÓDÓ DOKUMENTUMOK */}
            <section id="section-8" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500 flex items-center gap-3">
                <FileText className="text-blue-600" size={28} />
                8. KAPCSOLÓDÓ DOKUMENTUMOK
              </h2>

              <p className="text-gray-700 mb-6">
                A Szolgáltatás igénybevételére az alábbi dokumentumok vonatkoznak, amelyek a jelen Impresszummal együtt értelmezendők:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Dokumentum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Elérhetőség</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Leírás</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Általános Szerződési Feltételek (ÁSZF)</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <Link to="/terms" className="text-blue-600 hover:underline">Általános Szerződési Feltételek</Link>
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-sm">A Szolgáltatás igénybevételének általános feltételei, a felek jogai és kötelezettségei</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Adatvédelmi Tájékoztató</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <Link to="/privacy" className="text-blue-600 hover:underline">Adatkezelési Tájékoztató</Link>
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-sm">A személyes adatok kezelésére vonatkozó tájékoztató a GDPR és a magyar adatvédelmi jogszabályok alapján</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Cookie (Süti) Szabályzat</td>
                      <td className="border border-gray-300 px-4 py-3">
                        <Link to="/cookie-policy" className="text-blue-600 hover:underline">/cookie-szabalyzat</Link>
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-sm">A weboldalon alkalmazott sütikről szóló tájékoztató</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* 9. VONATKOZÓ JOGSZABÁLYOK */}
            <section id="section-9" className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-500">
                9. VONATKOZÓ JOGSZABÁLYOK
              </h2>

              <p className="text-gray-700 mb-6">
                A jelen Impresszum és a Szolgáltatás működése az alábbi jogszabályok rendelkezéseinek megfelelően történik:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">9.1. Magyar jogszabályok</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
                <li><strong>2001. évi CVIII. törvény</strong> az elektronikus kereskedelmi szolgáltatások, valamint az információs társadalommal összefüggő szolgáltatások egyes kérdéseiről (Eker. tv.)</li>
                <li><strong>2013. évi V. törvény</strong> a Polgári Törvénykönyvről (Ptk.)</li>
                <li><strong>2011. évi CXII. törvény</strong> az információs önrendelkezési jogról és az információszabadságról (Info tv.)</li>
                <li><strong>1999. évi LXXVI. törvény</strong> a szerzői jogról (Szjt.)</li>
                <li><strong>1997. évi CLV. törvény</strong> a fogyasztóvédelemről (Fgytv.)</li>
                <li><strong>45/2014. (II. 26.) Korm. rendelet</strong> a fogyasztó és a vállalkozás közötti szerződések részletes szabályairól</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">9.2. Európai uniós jogszabályok</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Az Európai Parlament és a Tanács (EU) 2016/679 rendelete</strong> (2016. április 27.) a természetes személyeknek a személyes adatok kezelése tekintetében történő védelméről és az ilyen adatok szabad áramlásáról (GDPR)</li>
                <li><strong>Az Európai Parlament és a Tanács 2002/58/EK irányelve</strong> (2002. július 12.) az elektronikus hírközlési ágazatban a személyes adatok kezeléséről, feldolgozásáról és a magánélet védelméről (ePrivacy irányelv)</li>
                <li><strong>Az Európai Parlament és a Tanács 2000/31/EK irányelve</strong> (2000. június 8.) a belső piacon az információs társadalommal összefüggő szolgáltatások, különösen az elektronikus kereskedelem, egyes jogi vonatkozásairól (E-kereskedelmi irányelv)</li>
              </ul>
            </section>

            {/* ZÁRADÉK */}
            <section className="mb-10 bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">ZÁRADÉK</h2>
              <p className="text-gray-700 mb-4">
                Jelen Impresszum a Weboldal és a Szolgáltatás üzemeltetésére vonatkozó törvényi tájékoztatási kötelezettséget 
                teljesíti a 2001. évi CVIII. törvény (Eker. tv.) 4. § alapján.
              </p>
              <p className="text-gray-700 mb-4">
                A dokumentum a Szolgáltató előzetes értesítése nélkül módosulhat. A hatályos verzió mindig a Weboldalon érhető el.
              </p>
              <div className="border-t border-gray-300 pt-4 mt-4">
                <p className="text-gray-600 text-sm"><strong>Utolsó módosítás:</strong> 2026.01.24.</p>
                <p className="text-gray-600 text-sm"><strong>Dokumentum verzió:</strong> 1.0</p>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
              <p>© 2026 ToldFel. Minden jog fenntartva.</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
